define((require) => {
    "use strict";

    const {
        debounce, pick, isEmpty, uniq, first, contains, find
    }                       = require("underscore");
    const EditorWebFontController = require("editor/ui/experience/editor/EditorWebFontController");
    const FontProvider            = require("shared/data/fonts/FontProvider");
    const GeneralFunctions        = require("editor/util/GeneralFunctions");
    const Storage                 = require("shared/services/Storage");
    const ViewController          = require("editor/util/ViewController");
    const WebFontsUtil            = require("shared/ui/experience/fonts/WebFontsUtil");
    const WebFontVariants         = require("shared/ui/experience/fonts/WebFontVariants").default;
    const CharacterPropertiesViewControllerHelpers =
        require("editor/ui/experience/widgets/CharacterPropertiesViewControllerHelpers");

    /**
     * For backwards compatibility, we need to set a font provider for legacy
     * text widgets.  It will be either a standard font or a Google Font. This
     * is a nop if the font provider is already present.
     *
     * @param  {TextWidgetModel} model  The <tt>TextWidgetModel</tt>.
     * @return {FontProvider}           The correct <tt>FontProvider</tt>.
     */
    const fontProviderFromModel = (model) => {
        const family = model.get("fontFamily");
        const providerValue = model.get("fontProvider");

        return FontProvider.fromValue(providerValue) || WebFontsUtil.fontProvider(family);
    };

    /**
     * Controller for the <tt>CharacterPropertiesView</tt>. Responsible for
     * fetching and managing font data.
     *
     * @class CharacterPropertiesViewController
     * @author Kim
     */
    return ViewController.extend({

        /**
         * @type {FontDescriptorCollection}
         */
        _fontCollection: CharacterPropertiesViewControllerHelpers._fontCollection,

        onInitialize() {
            EditorWebFontController.promise()
                .then((collection) => {
                    // Grab a reference to the collection of fonts
                    this._fontCollection = collection;

                    // Override the view's #fontSelectorQuery function to hook into the select control
                    this.view.fontSelectorQuery = this._fontSelectorQuery.bind(this);
                    this.view.fontSelectorInitSelection = this._fontSelectorInitSelection.bind(this);

                    // Create a debounced version of #_preloadFontsFromSearchTerm
                    this._preloadFontsFromSearchTerm = debounce(this._preloadFontsFromSearchTerm, 500).bind(this);

                    this.listenTo(this.model, "change", this._modelChangeHandler);
                });
        },

        _modelChangeHandler(model) {
            if (model.changed) {
                this._updateLastUsedProperties(model.changed);
            }

            if (model.changed.fontFamily) {
                this._updateFontVariants(model);
            }
        },

        _updateLastUsedProperties(changed) {
            let selectedData;
            let fontDescriptor;
            let favoriteFonts;
            const lastUsedProperties = pick(
                changed,
                "fontProvider",
                "color",
                "fontSize",
                "fontStyle",
                "fontMetadata",
                "fontWeight",
                "fontFamily"
            );

            if (isEmpty(lastUsedProperties)) {
                return;
            }

            if (!lastUsedProperties.fontFamily) {
                lastUsedProperties.fontProvider = lastUsedProperties.fontMetadata = null;
            }
            // Properties like font family can be null, we don't want to store those
            Storage.Local.update(Storage.Keys.LAST_USED_TEXT_PROPERTIES, GeneralFunctions.compactObject(lastUsedProperties));

            if (lastUsedProperties.fontFamily) {
                selectedData = this.view.getSelectedFontData();
                fontDescriptor = selectedData.fontDescriptor;

                favoriteFonts = Storage.Local.get(Storage.Keys.EDITOR, "favoriteFonts", []);

                favoriteFonts.unshift({
                    fontProvider: fontDescriptor.get("provider").value,
                    fontFamily: fontDescriptor.get("family")
                });

                // Remove duplicates
                favoriteFonts = uniq(favoriteFonts, false, (value) =>
                    `${value.fontFamily}:${value.fontProvider}`);

                // Store the (first 5) favorite fonts
                Storage.Local.update(Storage.Keys.EDITOR, "favoriteFonts", first(favoriteFonts, 5));
            }
        },

        /**
         * When the font selector (select2) changes its font family, we need
         * to update the font variants below it, so it only contains variants
         * that are valid with the current font family.
         *
         * If the font style is set to "regular 700" for the old font, then
         * we'd like to also pick the same font style by default for newly
         * selected font family if it's possible (the font may not have that
         * variation defined).
         *
         * The font variant select is updated by updating the editorModel's
         * fontWeight and fontStyle.
         *
         * @param {Object} font    A font object
         */
        _updateFontVariants(font) {
            // The new font.
            const fontFamily = font.get("fontFamily");

            // If multiple fonts are selected, this will be null. As will
            // fontWeight and fontStyle, so the select box should be blanked
            // out with no options.
            if (!fontFamily) {
                return;
            }

            // standard|typekit|google|fontsDotCom.
            const fontProviderValue = this.model.get("fontProvider");

            // fontProvider of the new font family.
            const fontProvider = FontProvider.fromValue(fontProviderValue);

            // Font variant describing fontStyle and fontWeight of the
            // selected font. (An object containing label, value, style,
            // and weight).
            // This is a a representation of the previously selected font
            // style and weight, based on the new font provider.
            const fontVariant = WebFontVariants.findVariant(
                fontProvider,
                this.model.get("fontStyle"),
                this.model.get("fontWeight")
            );

            // The value is the only needed thing. If the new font contains
            // a font variation with these weight and style, then the
            // variations dropdown should maintain its selection.
            const fontVariantValue = fontVariant && fontVariant.value;

            // Array of values of the different font variations belonging
            // to the selected font family.
            const fontVariations = this.getFontDescriptor(fontFamily, fontProvider).get("variations");

            // If our new font family can work with the previously selected
            // font variation, then we return without updating the model.
            if (contains(fontVariations, fontVariantValue)) {
                return;
            }

            // Failing that, we will attempt to select the "best" font
            // variation.
            const variants = WebFontVariants.fromValues(fontVariations, fontProvider);

            // Either select the first font variation in the list that has
            // a weight of 400, or, failing that, just select the first
            // font in the list (typically a very thin variation).
            const variant = find(variants, (variant) =>
                variant.weight === "400") || variants[0];

            // Update the editor model so controls update correctly.
            this.model.set({
                fontStyle: variant.style,
                fontWeight: variant.weight
            });
        },

        /**
         * @private
         *
         * Invoked initially from select2.
         *
         * @param  {HTMLElement}   element  The element Select2 is attached to.
         * @param  {Function} callback      Callback function that should be called with the data which is either
         *                                  an object in case of a single select or an array of objects in case of
         *                                  multi-select.
         *
         * @see  http://ivaynberg.github.io/select2/
         */
        _fontSelectorInitSelection(element, callback) {
            const fontFamily = this.model.get("fontFamily");
            let fontDescriptor;

            if (!fontFamily) {
                return;
            }

            fontDescriptor = this._fontCollection.fromFamily(fontFamily, fontProviderFromModel(this.model));

            if (!fontDescriptor) {
                // Covering edge case where someone removed their Typekit API token, and selects a text widget
                // that still points to a Typekit font family - fall back on a standard font family
                fontDescriptor = first(this._fontCollection.fromProvider(FontProvider.STANDARD));
                this.model.set({
                    fontProvider: FontProvider.STANDARD.value,
                    fontFamily: fontDescriptor.get("family"),
                    fontStyle: "normal",
                    fontWeight: "400"
                });
            }

            callback({
                id: fontFamily,
                text: fontDescriptor.get("name"),
                fontFamily,
                fontDescriptor
            });
        },

        /**
         * getFontDescriptor
         *
         * @param {String} fontFamily
         * @param {Object} fontProvider
         * @return {Array} fontFamilyVariants
         */
        getFontDescriptor(fontFamily, fontProvider) {
            return this._fontCollection.fromFamily(fontFamily, fontProvider);
        },
        _fontSelectorQuery: CharacterPropertiesViewControllerHelpers._fontSelectorQuery,
        _populateResult: CharacterPropertiesViewControllerHelpers._populateResult,
        _preloadFontsFromSearchTerm: CharacterPropertiesViewControllerHelpers._preloadFontsFromSearchTerm,
        _createOptions: CharacterPropertiesViewControllerHelpers._createOptions
    });
});



// WEBPACK FOOTER //
// ./src/editor/ui/experience/widgets/CharacterPropertiesViewController.js